/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  GripVertical,
  Eye,
  EyeOff,
  Copy,
  Trash2,
  Save,
  Plus,
  ArrowLeft,
  Settings,
  Layers,
  Code,
  Layout,
  ExternalLink,
} from 'lucide-react';
import axiosClient from '../api/axiosClient';
import SeoForm from '../components/SeoForm';
import toast from 'react-hot-toast';

const SECTION_TEMPLATES = [
  { key: 'hero', name: 'Hero Banner', defaultContent: { primaryCtaText: 'Book a Consultation', primaryCtaLink: '/book-consultation', secondaryCtaText: 'Explore Services', secondaryCtaLink: '/services', bgImage: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=1200' } },
  { key: 'services', name: 'Services Grid', defaultContent: { displayCount: 6, showAllLink: true } },
  { key: 'advantages', name: 'Why Partner With Us', defaultContent: { items: [{ title: '5-Minute Response', description: 'Real human support within minutes.' }, { title: '100% SLA Guarantee', description: 'Enterprise uptime guaranteed.' }] } },
  { key: 'testimonials', name: 'Testimonials Slider', defaultContent: { displayCount: 3 } },
  { key: 'cta', name: 'Call To Action Banner', defaultContent: { ctaText: 'Get Started Today', ctaLink: '/contact', bgGradient: 'from-blue-900 to-indigo-900' } },
  { key: 'custom_section', name: 'Custom Rich Block', defaultContent: { text: 'Custom section content' } },
];

export default function PageBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [seoData, setSeoData] = useState({});
  const [activeTab, setActiveTab] = useState('sections');
  const [saving, setSaving] = useState(false);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [editorMode, setEditorMode] = useState('visual'); // 'visual' | 'json'

  useEffect(() => {
    fetchPage();
  }, [id]);

  const fetchPage = async () => {
    try {
      const res = await axiosClient.get('/pages');
      const found = res.data.find(p => p.id === id || p.slug === id);
      if (found) {
        setPage(found);
        setSections(found.sections || []);
        setSeoData({
          seoTitle: found.seoTitle || '',
          metaDescription: found.metaDescription || '',
          keywords: found.keywords || '',
          canonicalUrl: found.canonicalUrl || '',
          ogImage: found.ogImage || '',
          twitterCard: found.twitterCard || 'summary_large_image',
          robots: found.robots || 'index, follow',
        });
      }
    } catch (err) {
      toast.error('Failed to load page.');
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(sections);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    setSections(items);
    setSelectedSectionIndex(result.destination.index);
  };

  const toggleSection = (idx) => {
    const updated = [...sections];
    updated[idx].isEnabled = !updated[idx].isEnabled;
    setSections(updated);
  };

  const duplicateSection = (sec, idx) => {
    const newSec = {
      ...sec,
      id: `temp-${Date.now()}`,
      title: `${sec.title || 'Section'} (Copy)`,
    };
    const updated = [...sections];
    updated.splice(idx + 1, 0, newSec);
    setSections(updated);
    toast.success('Section duplicated.');
  };

  const deleteSection = (idx) => {
    if (sections.length <= 1) {
      toast.error('Page must contain at least one section.');
      return;
    }
    const updated = [...sections];
    updated.splice(idx, 1);
    setSections(updated);
    setSelectedSectionIndex(Math.max(0, idx - 1));
    toast.success('Section removed.');
  };

  const addSection = (tpl) => {
    const newSec = {
      id: `temp-${Date.now()}`,
      sectionKey: tpl.key,
      title: tpl.name,
      subtitle: 'Section description subtitle',
      content: JSON.stringify(tpl.defaultContent),
      isEnabled: true,
    };
    setSections([...sections, newSec]);
    setSelectedSectionIndex(sections.length);
    toast.success(`Added ${tpl.name} block.`);
  };

  const parseCurrentContent = () => {
    if (!sections[selectedSectionIndex]) return {};
    const raw = sections[selectedSectionIndex].content;
    if (typeof raw === 'object' && raw !== null) return raw;
    try {
      return JSON.parse(raw || '{}');
    } catch (e) {
      return {};
    }
  };

  const updateSelectedContent = (newContentObj) => {
    const updated = [...sections];
    updated[selectedSectionIndex].content = JSON.stringify(newContentObj);
    setSections(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosClient.put(`/pages/${page.id}/sections`, { sections });
      await axiosClient.put(`/pages/${page.id}/seo`, seoData);
      toast.success('Page layout and SEO saved successfully!');
      fetchPage();
    } catch (err) {
      toast.error('Failed to save page changes.');
    } finally {
      setSaving(false);
    }
  };

  if (!page) return <div className="p-8 text-center text-slate-400">Loading page builder...</div>;

  const currentSection = sections[selectedSectionIndex] || null;
  const currentContent = parseCurrentContent();

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-800">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/pages')}
            className="p-2 rounded-xl bg-slate-900 border border-slate-800 hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="text-2xl font-black text-white">{page.title}</h1>
              <span className="text-xs font-mono text-blue-400 bg-blue-500/10 px-2.5 py-0.5 rounded-full border border-blue-500/20">
                /{page.slug}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">Visual drag & drop section builder and layout manager</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <a
            href={`/${page.slug === 'home' ? '' : page.slug}`}
            target="_blank"
            rel="noreferrer"
            className="px-3.5 py-2.5 rounded-xl font-bold text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 flex items-center space-x-1.5 transition-colors border border-slate-700"
          >
            <span>Live View</span>
            <ExternalLink size={13} />
          </a>

          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 rounded-xl font-bold text-xs bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/30 flex items-center space-x-2 transition-all"
          >
            <Save size={16} />
            <span>{saving ? 'Saving...' : 'Save Page Layout'}</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center space-x-2 border-b border-slate-800 pb-1">
        <button
          onClick={() => setActiveTab('sections')}
          className={`px-4 py-2.5 font-bold text-xs rounded-xl flex items-center space-x-2 transition-all ${
            activeTab === 'sections'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'
          }`}
        >
          <Layers size={16} />
          <span>Section Builder ({sections.length})</span>
        </button>
        <button
          onClick={() => setActiveTab('seo')}
          className={`px-4 py-2.5 font-bold text-xs rounded-xl flex items-center space-x-2 transition-all ${
            activeTab === 'seo'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'
          }`}
        >
          <Settings size={16} />
          <span>SEO & SERP Metadata</span>
        </button>
      </div>

      {/* Sections Drag & Drop View */}
      {activeTab === 'sections' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: Sections List */}
          <div className="lg:col-span-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Page Section Stack</span>
              <div className="relative group">
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold flex items-center space-x-1 shadow-md transition-all"
                >
                  <Plus size={14} />
                  <span>Add Block</span>
                </button>

                {/* Template Menu */}
                <div className="absolute left-0 mt-1 w-48 bg-slate-900 border border-slate-800 rounded-xl shadow-2xl p-1.5 hidden group-hover:block z-50">
                  {SECTION_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.key}
                      onClick={() => addSection(tpl)}
                      className="w-full text-left px-3 py-2 text-xs text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors font-medium flex items-center justify-between"
                    >
                      <span>{tpl.name}</span>
                      <span className="text-[10px] text-slate-500 font-mono">+{tpl.key}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="sections-list">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2.5">
                    {sections.map((sec, index) => {
                      const isSelected = selectedSectionIndex === index;
                      return (
                        <Draggable key={sec.id || `sec-${index}`} draggableId={sec.id || `sec-${index}`} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              onClick={() => setSelectedSectionIndex(index)}
                              className={`p-3.5 rounded-2xl border transition-all cursor-pointer ${
                                snapshot.isDragging
                                  ? 'bg-slate-800 border-blue-500 shadow-2xl scale-102 z-50'
                                  : sec.isEnabled === false
                                  ? 'bg-slate-950/40 border-slate-900 opacity-60'
                                  : isSelected
                                  ? 'bg-slate-900 border-blue-500 shadow-lg ring-1 ring-blue-500/40'
                                  : 'bg-slate-900/90 border-slate-800 hover:border-slate-700'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div {...provided.dragHandleProps} className="cursor-grab text-slate-500 hover:text-slate-300">
                                    <GripVertical size={18} />
                                  </div>
                                  <div className="flex flex-col">
                                    <div className="flex items-center space-x-2">
                                      <span className="font-bold text-xs text-white">{sec.title || 'Untitled Section'}</span>
                                      <span className="text-[10px] font-mono text-blue-400 uppercase bg-blue-500/10 px-1.5 py-0.5 rounded border border-blue-500/20">
                                        {sec.sectionKey}
                                      </span>
                                    </div>
                                    <span className="text-[11px] text-slate-400 truncate max-w-[220px]">
                                      {sec.subtitle || 'No subtitle configured'}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex items-center space-x-1">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); toggleSection(index); }}
                                    className={`p-1.5 rounded-lg text-xs transition-colors ${
                                      sec.isEnabled !== false ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-slate-500 hover:bg-slate-800'
                                    }`}
                                    title={sec.isEnabled !== false ? 'Enabled' : 'Disabled'}
                                  >
                                    {sec.isEnabled !== false ? <Eye size={15} /> : <EyeOff size={15} />}
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); duplicateSection(sec, index); }}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                                    title="Duplicate"
                                  >
                                    <Copy size={15} />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); deleteSection(index); }}
                                    className="p-1.5 rounded-lg text-rose-400 hover:bg-rose-500/10 transition-colors"
                                    title="Delete"
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>

          {/* Right Column: Visual Section Inspector */}
          <div className="lg:col-span-7 bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl flex flex-col justify-between">
            {currentSection ? (
              <div className="space-y-5">
                {/* Inspector Header */}
                <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                  <div>
                    <h3 className="font-bold text-sm text-white flex items-center space-x-2">
                      <span>Edit: {currentSection.title}</span>
                      <span className="text-[10px] font-mono text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">
                        {currentSection.sectionKey}
                      </span>
                    </h3>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setEditorMode('visual')}
                      className={`px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center space-x-1 transition-all ${
                        editorMode === 'visual'
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-400 hover:bg-slate-800'
                      }`}
                    >
                      <Layout size={13} />
                      <span>Visual Editor</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditorMode('json')}
                      className={`px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center space-x-1 transition-all ${
                        editorMode === 'json'
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-400 hover:bg-slate-800'
                      }`}
                    >
                      <Code size={13} />
                      <span>Raw JSON</span>
                    </button>
                  </div>
                </div>

                {/* Common Section Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Section Headline / Title</label>
                    <input
                      type="text"
                      value={currentSection.title || ''}
                      onChange={(e) => {
                        const updated = [...sections];
                        updated[selectedSectionIndex].title = e.target.value;
                        setSections(updated);
                      }}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Section Subtitle</label>
                    <input
                      type="text"
                      value={currentSection.subtitle || ''}
                      onChange={(e) => {
                        const updated = [...sections];
                        updated[selectedSectionIndex].subtitle = e.target.value;
                        setSections(updated);
                      }}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Content Editor: Visual vs JSON */}
                {editorMode === 'visual' ? (
                  <div className="space-y-4 pt-2">
                    {/* Hero Section Visual Form */}
                    {currentSection.sectionKey === 'hero' && (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-slate-300 mb-1">Primary CTA Button Text</label>
                            <input
                              type="text"
                              value={currentContent.primaryCtaText || ''}
                              onChange={(e) => updateSelectedContent({ ...currentContent, primaryCtaText: e.target.value })}
                              placeholder="Book Consultation"
                              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-slate-300 mb-1">Primary CTA Link</label>
                            <input
                              type="text"
                              value={currentContent.primaryCtaLink || ''}
                              onChange={(e) => updateSelectedContent({ ...currentContent, primaryCtaLink: e.target.value })}
                              placeholder="/book-consultation"
                              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500 font-mono"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-slate-300 mb-1">Secondary CTA Text</label>
                            <input
                              type="text"
                              value={currentContent.secondaryCtaText || ''}
                              onChange={(e) => updateSelectedContent({ ...currentContent, secondaryCtaText: e.target.value })}
                              placeholder="Explore Services"
                              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-slate-300 mb-1">Secondary CTA Link</label>
                            <input
                              type="text"
                              value={currentContent.secondaryCtaLink || ''}
                              onChange={(e) => updateSelectedContent({ ...currentContent, secondaryCtaLink: e.target.value })}
                              placeholder="/services"
                              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500 font-mono"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-slate-300 mb-1">Hero Background Image URL</label>
                          <input
                            type="text"
                            value={currentContent.bgImage || ''}
                            onChange={(e) => updateSelectedContent({ ...currentContent, bgImage: e.target.value })}
                            placeholder="https://images.unsplash.com/..."
                            className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500"
                          />
                        </div>
                      </>
                    )}

                    {/* CTA Section Visual Form */}
                    {currentSection.sectionKey === 'cta' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-slate-300 mb-1">CTA Button Label</label>
                          <input
                            type="text"
                            value={currentContent.ctaText || ''}
                            onChange={(e) => updateSelectedContent({ ...currentContent, ctaText: e.target.value })}
                            className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-300 mb-1">CTA Destination URL</label>
                          <input
                            type="text"
                            value={currentContent.ctaLink || ''}
                            onChange={(e) => updateSelectedContent({ ...currentContent, ctaLink: e.target.value })}
                            className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500 font-mono"
                          />
                        </div>
                      </div>
                    )}

                    {/* Generic block visual editor */}
                    {!['hero', 'cta'].includes(currentSection.sectionKey) && (
                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1">Block Parameters</label>
                        <textarea
                          rows={6}
                          value={JSON.stringify(currentContent, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              updateSelectedContent(parsed);
                            } catch (err) {}
                          }}
                          className="w-full p-3 rounded-xl border border-slate-800 bg-slate-950 text-cyan-400 font-mono text-xs focus:outline-none focus:border-blue-500"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Raw JSON Payload</label>
                    <textarea
                      rows={10}
                      value={
                        typeof currentSection.content === 'object'
                          ? JSON.stringify(currentSection.content, null, 2)
                          : currentSection.content || '{}'
                      }
                      onChange={(e) => {
                        const updated = [...sections];
                        updated[selectedSectionIndex].content = e.target.value;
                        setSections(updated);
                      }}
                      className="w-full p-3 rounded-xl border border-slate-800 bg-slate-950 text-emerald-400 font-mono text-xs focus:outline-none focus:border-blue-500"
                    />
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-slate-500 text-center py-12">Select a section from the left stack to configure.</p>
            )}
          </div>
        </div>
      )}

      {/* SEO Tab */}
      {activeTab === 'seo' && (
        <SeoForm seoData={seoData} onChange={setSeoData} />
      )}
    </div>
  );
}
